import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Toast.css';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastMessage {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
  progress?: boolean;
}

interface ToastProps {
  toast: ToastMessage;
  onRemove: (id: string) => void;
}

const MAX_VISIBLE_TOASTS = 3;
const DEFAULT_DURATION = 5000;

const Toast: React.FC<ToastProps> = ({ toast, onRemove }) => {
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const startTimeRef = useRef<number>(Date.now());
  const duration = toast.duration || DEFAULT_DURATION;
  const showProgress = toast.progress !== false;

  // Handle removal with exit animation
  const handleRemove = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onRemove(toast.id);
    }, 300); // Match animation duration
  }, [toast.id, onRemove]);

  // Auto-dismiss timer with progress
  useEffect(() => {
    if (duration <= 0) return;

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
        handleRemove();
      }
    }, 50);

    return () => clearInterval(interval);
  }, [duration, handleRemove]);

  // Touch handling for swipe to dismiss
  const touchStartRef = useRef<number>(0);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchEnd - touchStartRef.current;
    
    // Swipe right to dismiss
    if (diff > 100) {
      handleRemove();
    }
  };

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return (
          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
        );
      case 'error':
        return (
          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        );
      case 'warning':
        return (
          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
          </svg>
        );
      case 'info':
      default:
        return (
          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
          </svg>
        );
    }
  };

  return (
    <div 
      className={`toast toast-${toast.type} ${isExiting ? 'toast-exit' : ''}`}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      role="alert"
      aria-live="polite"
    >
      <span className="toast-icon">{getIcon()}</span>
      <span className="toast-message">{toast.message}</span>
      <button 
        className="toast-close" 
        onClick={handleRemove}
        aria-label="Dismiss notification"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>
      {showProgress && duration > 0 && (
        <div className="toast-progress">
          <div 
            className="toast-progress-bar" 
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};

// Toast container component with queue management
const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const queueRef = useRef<ToastMessage[]>([]);

  // Process queue when visible toasts decrease
  useEffect(() => {
    if (toasts.length < MAX_VISIBLE_TOASTS && queueRef.current.length > 0) {
      const nextToast = queueRef.current.shift()!;
      setToasts(prev => [...prev, nextToast]);
    }
  }, [toasts.length]);

  useEffect(() => {
    const handleShowToast = (event: CustomEvent<{ 
      message: string; 
      type: ToastType; 
      duration?: number;
      progress?: boolean;
    }>) => {
      const newToast: ToastMessage = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        message: event.detail.message,
        type: event.detail.type,
        duration: event.detail.duration,
        progress: event.detail.progress,
      };

      // If under max, show immediately; otherwise queue
      setToasts(prev => {
        if (prev.length < MAX_VISIBLE_TOASTS) {
          return [...prev, newToast];
        } else {
          queueRef.current.push(newToast);
          return prev;
        }
      });
    };

    window.addEventListener('show-toast', handleShowToast as EventListener);
    return () => window.removeEventListener('show-toast', handleShowToast as EventListener);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Clear all toasts
  useEffect(() => {
    const handleClearToasts = () => {
      setToasts([]);
      queueRef.current = [];
    };

    window.addEventListener('clear-toasts', handleClearToasts);
    return () => window.removeEventListener('clear-toasts', handleClearToasts);
  }, []);

  return (
    <div className="toast-container" role="region" aria-label="Notifications">
      {toasts.map((toast, index) => (
        <Toast 
          key={toast.id} 
          toast={toast} 
          onRemove={removeToast}
        />
      ))}
      {queueRef.current.length > 0 && (
        <div className="toast-queue-indicator">
          +{queueRef.current.length} more
        </div>
      )}
    </div>
  );
};

// Helper function to show a toast
export const showToast = (
  message: string, 
  type: ToastType = 'info', 
  duration?: number,
  progress: boolean = true
) => {
  const event = new CustomEvent('show-toast', {
    detail: { message, type, duration, progress },
  });
  window.dispatchEvent(event);
};

// Helper to clear all toasts
export const clearToasts = () => {
  window.dispatchEvent(new CustomEvent('clear-toasts'));
};

// Convenience methods
export const toast = {
  success: (message: string, duration?: number) => showToast(message, 'success', duration),
  error: (message: string, duration?: number) => showToast(message, 'error', duration || 7000),
  warning: (message: string, duration?: number) => showToast(message, 'warning', duration),
  info: (message: string, duration?: number) => showToast(message, 'info', duration),
  clear: clearToasts,
};

export default ToastContainer;
